import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, ChefHat, Clock, Flame, Users, Wine } from 'lucide-react';
import {
  COURSE_LABELS,
  type OrderItemStatus,
  type ProductionTicket,
  type TicketedDestination,
} from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useTickets } from '../hooks/queries';
import { useNow } from '../hooks/useNow';
import { useToast } from '../hooks/useToast';
import { formatClock, minutesSince } from '../lib/format';
import { Button, EmptyState, ErrorState, Spinner } from '../components/ui';

/**
 * Age thresholds drive the colour of a ticket. The kitchen reads urgency from
 * across the pass, so the signal is the card border, not a small label.
 */
function urgency(minutes: number): 'fresh' | 'warm' | 'late' {
  if (minutes >= 20) return 'late';
  if (minutes >= 10) return 'warm';
  return 'fresh';
}

const URGENCY_STYLE = {
  fresh: 'border-line',
  warm: 'border-caution/60',
  late: 'border-danger/70 shadow-[0_0_0_1px_rgba(204,95,86,0.35)]',
} as const;

const STATION_META: Record<TicketedDestination, { title: string; icon: typeof ChefHat }> = {
  KITCHEN: { title: 'Kitchen', icon: ChefHat },
  BAR: { title: 'Bar', icon: Wine },
};

function TicketCard({
  ticket,
  now,
  onItemStatus,
  onTicketStatus,
  busy,
}: {
  ticket: ProductionTicket;
  now: number;
  onItemStatus: (itemId: string, status: OrderItemStatus) => void;
  onTicketStatus: (ticketId: string, status: string) => void;
  busy: boolean;
}) {
  const waited = minutesSince(ticket.sentAt, now);
  const tone = urgency(waited);

  const byCourse = new Map<number, typeof ticket.items>();
  for (const item of ticket.items) {
    if (item.status === 'CANCELLED') continue;
    byCourse.set(item.course, [...(byCourse.get(item.course) ?? []), item]);
  }
  const courses = [...byCourse.keys()].sort((a, b) => a - b);
  const live = ticket.items.filter((item) => item.status !== 'CANCELLED');
  const allReady = live.length > 0 && live.every((item) => item.status === 'READY' || item.status === 'SERVED');

  return (
    <article
      className={clsx(
        'flex flex-col rounded-card border-2 bg-surface',
        URGENCY_STYLE[tone],
        ticket.status === 'READY' && 'border-positive/70',
      )}
    >
      <header className="border-b border-line px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display text-3xl leading-none text-ink">
              Table {String(ticket.tableNumber).padStart(2, '0')}
            </div>
            <div className="mt-1 text-xs text-ink-faint">
              Order #{ticket.orderNumber} · Ticket #{ticket.ticketNumber}
            </div>
          </div>
          <div className="text-right">
            <div
              className={clsx(
                'tnum font-display text-2xl leading-none',
                tone === 'late' ? 'text-danger' : tone === 'warm' ? 'text-caution' : 'text-ink',
              )}
            >
              {String(waited).padStart(2, '0')}
              <span className="ml-0.5 text-sm">min</span>
            </div>
            <div className="mt-1 text-[11px] text-ink-faint">Sent {formatClock(ticket.sentAt)}</div>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ink-faint">
          <span className="inline-flex items-center gap-1">
            <Users size={11} /> {ticket.guestCount} guests
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock size={11} /> Opened {formatClock(ticket.orderOpenedAt)}
          </span>
          {ticket.sentByName ? <span>by {ticket.sentByName}</span> : null}
        </div>

        {ticket.orderNotes ? (
          <p className="mt-2 rounded border border-caution/40 bg-caution/10 px-2 py-1 text-[11px] text-caution">
            {ticket.orderNotes}
          </p>
        ) : null}
      </header>

      <div className="flex-1 space-y-4 px-4 py-3">
        {courses.map((course) => (
          <section key={course}>
            <div className="mb-1.5 flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-accent">
                {COURSE_LABELS[course]}
              </span>
              <div className="rule-accent flex-1 opacity-40" />
            </div>
            <ul className="space-y-2">
              {byCourse.get(course)!.map((item) => (
                <li key={item.id}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div
                        className={clsx(
                          'text-base font-medium leading-snug',
                          item.status === 'READY' || item.status === 'SERVED'
                            ? 'text-ink-faint line-through'
                            : 'text-ink',
                        )}
                      >
                        <span className="tnum text-accent">{item.quantity}×</span> {item.nameSnapshot}
                      </div>
                      {item.modifiers.length > 0 ? (
                        <div className="text-xs text-ink-soft">
                          {item.modifiers
                            .map((modifier) =>
                              modifier.quantity > 1
                                ? `${modifier.quantity}× ${modifier.optionName}`
                                : modifier.optionName,
                            )
                            .join(' · ')}
                        </div>
                      ) : null}
                      {item.notes ? (
                        <div className="mt-0.5 text-xs font-medium italic text-caution">
                          ▸ {item.notes}
                        </div>
                      ) : null}
                    </div>

                    <div className="flex shrink-0 gap-1">
                      {item.status === 'SENT' ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => onItemStatus(item.id, 'PREPARING')}
                          className="h-9 rounded-lg border border-line px-2.5 text-xs font-semibold text-ink-soft transition hover:border-reserved/60 hover:text-reserved"
                        >
                          Start
                        </button>
                      ) : null}
                      {item.status === 'SENT' || item.status === 'PREPARING' ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => onItemStatus(item.id, 'READY')}
                          className="h-9 rounded-lg border border-positive/50 bg-positive/10 px-2.5 text-xs font-semibold text-positive transition hover:bg-positive/20"
                        >
                          Ready
                        </button>
                      ) : null}
                      {item.status === 'READY' ? (
                        <span className="inline-flex h-9 items-center gap-1 rounded-lg border border-positive/40 px-2.5 text-xs font-semibold text-positive">
                          <Check size={13} /> Ready
                        </span>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <footer className="flex gap-2 border-t border-line px-4 py-3">
        {ticket.status === 'NEW' ? (
          <Button
            variant="secondary"
            block
            disabled={busy}
            icon={<Flame size={16} />}
            onClick={() => onTicketStatus(ticket.id, 'PREPARING')}
          >
            Start all
          </Button>
        ) : null}
        {!allReady && ticket.status !== 'READY' ? (
          <Button variant="success" block disabled={busy} onClick={() => onTicketStatus(ticket.id, 'READY')}>
            All ready
          </Button>
        ) : (
          <Button variant="primary" block disabled={busy} onClick={() => onTicketStatus(ticket.id, 'COMPLETED')}>
            Bump ticket
          </Button>
        )}
      </footer>
    </article>
  );
}

/** Shared implementation of the Kitchen and Bar display systems. */
export function StationPage({ destination }: { destination: TicketedDestination }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const now = useNow(10_000);
  const [showRecent, setShowRecent] = useState(false);

  const meta = STATION_META[destination];
  const { data: tickets, isLoading, error, refetch } = useTickets(
    destination,
    showRecent ? 30 : 0,
  );

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['tickets', destination] });

  const itemStatus = useMutation({
    mutationFn: ({ itemId, status }: { itemId: string; status: OrderItemStatus }) =>
      api.station(destination).setItemStatus(itemId, status),
    onSuccess: invalidate,
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not update that item.'),
  });

  const ticketStatus = useMutation({
    mutationFn: ({ ticketId, status }: { ticketId: string; status: string }) =>
      api.station(destination).setTicketStatus(ticketId, status),
    onSuccess: invalidate,
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not update that ticket.'),
  });

  const stats = useMemo(() => {
    const list = tickets ?? [];
    const active = list.filter((ticket) => ticket.status !== 'COMPLETED');
    const oldest = active.reduce<number>(
      (max, ticket) => Math.max(max, minutesSince(ticket.sentAt, now)),
      0,
    );
    return {
      active: active.length,
      items: active.reduce(
        (sum, ticket) => sum + ticket.items.filter((item) => item.status !== 'CANCELLED').length,
        0,
      ),
      oldest,
    };
  }, [tickets, now]);

  const busy = itemStatus.isPending || ticketStatus.isPending;

  return (
    <div className="mx-auto max-w-[1800px] px-5 py-6">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-accent/40 bg-accent/10">
            <meta.icon size={22} className="text-accent-strong" />
          </div>
          <div>
            <h1 className="font-display text-3xl tracking-wide text-ink">{meta.title} Display</h1>
            <p className="mt-0.5 text-sm text-ink-faint">
              {stats.active} open ticket{stats.active === 1 ? '' : 's'} · {stats.items} item
              {stats.items === 1 ? '' : 's'}
              {stats.oldest > 0 ? ` · oldest ${stats.oldest} min` : ''}
            </p>
          </div>
        </div>

        <Button variant="secondary" onClick={() => setShowRecent((value) => !value)}>
          {showRecent ? 'Hide completed' : 'Show recently bumped'}
        </Button>
      </header>

      {isLoading ? <Spinner label="Loading tickets…" /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {!isLoading && !error && (tickets?.length ?? 0) === 0 ? (
        <EmptyState
          icon={<meta.icon size={34} />}
          title="All clear"
          message={`No tickets waiting at the ${meta.title.toLowerCase()} right now.`}
        />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {(tickets ?? []).map((ticket) => (
          <TicketCard
            key={ticket.id}
            ticket={ticket}
            now={now}
            busy={busy}
            onItemStatus={(itemId, status) => itemStatus.mutate({ itemId, status })}
            onTicketStatus={(ticketId, status) => ticketStatus.mutate({ ticketId, status })}
          />
        ))}
      </div>
    </div>
  );
}
